'use client';

import { MutableRefObject, useEffect, useState } from 'react';

import { fetchApiClientDestructData } from '@/api/fetchers/fetchApiClient';

export enum ChartType {
  YIELD = 'yield',
  EQUITY = 'equity',
  FOLLOWERS_EQUITY = 'followers_equity',
  MARGIN = 'margin',
  PROFITABILITY = 'profitability',
}

export enum Interval {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  ALL = 'ALL',
  PROFITABILITY_DAY = 'PROFITABILITY_DAY',
  PROFITABILITY_MONTH = 'PROFITABILITY_MONTH',
}

export const INTERVALS = {
  [Interval.DAY]: {
    barSize: 60,
    urlParam: '1m',
  },
  [Interval.WEEK]: {
    barSize: 600,
    urlParam: '10m',
  },
  [Interval.MONTH]: {
    barSize: 3600,
    urlParam: '1h',
  },
  [Interval.ALL]: {
    barSize: 3600,
    urlParam: 'all',
  },
  [Interval.PROFITABILITY_DAY]: {
    barSize: 0,
    urlParam: 'day',
  },
  [Interval.PROFITABILITY_MONTH]: {
    barSize: 0,
    urlParam: 'month',
  },
};

function getChartTypeDataMapper(chartType: ChartType) {
  switch (chartType) {
    case ChartType.MARGIN:
      return (i: [number, number]) => [i[0], 100 - i[1]];
    default:
      return null;
  }
}

export function useStrategyChartData({
  strategyId,
  onChange,
  refChartContainer,
  defaultInterval = Interval.ALL,
  defaultChartType = ChartType.YIELD,
}: {
  strategyId: number;
  onChange: (data: any) => void;
  refChartContainer: MutableRefObject<null | HTMLElement>;
  defaultInterval?: Interval;
  defaultChartType?: ChartType;
}) {
  const [chartType, setChartType] = useState<ChartType>(defaultChartType);
  const [interval, setInterval] = useState<Interval>(defaultInterval);
  const [chartMounted, setChartMounted] = useState<boolean>(false);

  useEffect(() => {
    if (interval === Interval.ALL && !chartMounted) {
      return;
    }

    let url = `/v2/public/covesting/strategies/${strategyId}/${chartType}/history`;

    if (interval === Interval.ALL) {
      const canvas = refChartContainer.current?.querySelector('canvas');

      if (!canvas) {
        return console.error('Cannot get half chart canvas size to fetch data with ?Limit');
      }

      const canvasWidth = parseFloat(window.getComputedStyle(canvas).getPropertyValue('width'));
      const halfChartCanvasWidth = Math.round(canvasWidth / 2);
      const search = new URLSearchParams({ limit: halfChartCanvasWidth.toString() });

      url += '?' + search.toString();
    } else {
      url += `/${INTERVALS[interval].urlParam}`;
    }

    fetchApiClientDestructData<[number, number][]>(url).then((data) => {
      if (!data) {
        return;
      }
      const mapper = getChartTypeDataMapper(chartType);
      return onChange(mapper ? data.map(mapper) : data);
    });
  }, [chartMounted, chartType, interval, onChange, refChartContainer, strategyId]);

  return {
    setInterval,
    setChartType,
    setChartMounted,
    chartType,
    interval,
  };
}
